import axios from "axios";
import Cookies from 'js-cookie'
import { withApiError, withApiErrorResponse } from "../utils/apiError";

const BASE_URL = process.env.REACT_APP_BASE_BACKEND_API_URL;

export const fetchTaskDetails = async (taskId) => {
  const token = Cookies.get("accessToken");
  return fetch(`${BASE_URL}api/v1/task/web/get-task?taskId=${taskId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      const value = data?.body?.value;
      // API can return either:
      // 1) value = task object (old shape)
      // 2) value = { assignees: [], task: {...} } (new shape)
      const rawTask = value?.task ? value.task : value;
      if (!rawTask || typeof rawTask !== "object") return null;

      const taskDetail = {
        ...rawTask,
        assignees: Array.isArray(value?.assignees)
          ? value.assignees
          : Array.isArray(rawTask.assignees)
            ? rawTask.assignees
            : [],
      };

      try {
        if (taskDetail.taskDetailsJson && typeof taskDetail.taskDetailsJson === "object") {
          taskDetail.taskDetailsParsed = taskDetail.taskDetailsJson;
        } else if (taskDetail.taskDetailsJson && typeof taskDetail.taskDetailsJson === "string") {
          taskDetail.taskDetailsParsed = JSON.parse(taskDetail.taskDetailsJson);
        } else if (taskDetail.taskDetails && typeof taskDetail.taskDetails === "string") {
          taskDetail.taskDetailsParsed = JSON.parse(taskDetail.taskDetails);
        } else {
          taskDetail.taskDetailsParsed = {};
        }
      } catch (e) {
        console.warn("Unable to parse task details json:", e);
        taskDetail.taskDetailsParsed = {};
      }

      return taskDetail;
    })
    .catch((error) => {
      console.error("Error fetching data:", error);
      throw withApiErrorResponse(error); // Propagate the error further if needed
    });
};

// Function to fetch field agents
export const fetchFieldAgents = async () => {
  const token = Cookies.get("accessToken");
  return axios.get(`${BASE_URL}api/v1/user/field-agents`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      return response.data.body.value;
    })
    .catch((error) => {
      console.error("An error occurred:", error);
      throw withApiErrorResponse(error); // Propagate the error further if needed
    });
};

// Function to assign task to field agent
export const assignTaskToAgent = async (taskIds, selectedFieldAgent, workflowId) => {
  const token = Cookies.get("accessToken");
  const requestData = {
    taskIdList: taskIds,
    fieldAgentId: selectedFieldAgent,
    workflowId: workflowId,
  };

  return axios.post(`${BASE_URL}api/v1/task/multi-assign`, requestData, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      return withApiError(response.data);
    })
    .catch((error) => {
      console.error("Error:", error);
      throw withApiErrorResponse(error); // Propagate the error further if needed
    });
};

// Function to fetch tasks based on filters
export const fetchTasksByFilters = async (workflowId, taskStatus) => {
  const token = Cookies.get("accessToken");
  return axios.get(`${BASE_URL}api/v1/task/map/get-tasks?workflowId=${workflowId}&taskStatus=${taskStatus}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      return response.data.body.value.taskList.map((task) => ({
        lat: parseFloat(task.latitude),
        lng: parseFloat(task.longitude),
        taskid: task.taskId,
        accountNo: task.accountNo,
      }));
    })
    .catch((error) => {
      console.error("An error occurred:", error);
      throw withApiErrorResponse(error); // Propagate the error further if needed
    });
};

export const getTaskAPI = async (workflowId, taskStatus, assigneeId = "") => {
  const token = Cookies.get("accessToken");
  try {
    // Constructing the URL with workflowId, taskStatus, and optional assigneeId
    const url = `${BASE_URL}api/v1/task/map/get-tasks?workflowId=${workflowId}&taskStatus=${taskStatus}${assigneeId ? `&assigneeId=${assigneeId}` : ""
      }`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return null;
  }
};
// get task data
export const getTaskListAPI = async (workflowId, taskStatus, assigneeId = "", fromDate = '', toDate = '',accountNo) => {
  const token = Cookies.get("accessToken");
  try {
    const url = `${BASE_URL}api/v1/task/map/get-tasks?workflowIds=${workflowId}${accountNo ? `&accountNo=${accountNo}` : ""}${taskStatus ? `&taskStatus=${taskStatus}` : ""}${assigneeId ? `&assigneeId=${assigneeId}` : ""
      }${fromDate ? `&fromDate=${fromDate}` : ""}${toDate ? `&toDate=${toDate}` : ""}`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return null;
  }
};

export const getTaskAPI2 = async (workflowId, taskStatus, assigneeId = "") => {
  const token = Cookies.get("accessToken");
  try {
    // Constructing the URL with optional assigneeId
    const url = `${BASE_URL}api/v1/task/map/get-tasks?workflowId=${workflowId}&taskStatus=${assigneeId ? `&assigneeId=${assigneeId}` : ""
      }`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return null;
  }
};

/**
 * Fetch tracking data for map view.
 * @param {number|string} workflowId - Required workflow id
 * @param {number|string} [fieldAgentId] - Optional field agent filter
 * @param {string} [date] - Optional date filter (ISO string e.g. 2026-02-12T09:51:31)
 * @returns {Promise<{ body: { value: Array } }|null>}
 */
export const fetchTracking = async (workflowId, fieldAgentId = "", date = "") => {
  const token = Cookies.get("accessToken");
  try {
    const params = new URLSearchParams();
    params.set("workflowId", workflowId);
    if (fieldAgentId != null && String(fieldAgentId).trim() !== "") {
      params.set("fieldAgentId", String(fieldAgentId).trim());
    }
    if (date != null && String(date).trim() !== "") {
      params.set("date", String(date).trim());
    }
    const url = `${BASE_URL}api/v1/task/tracking?${params.toString()}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching tracking:", error);
    return null;
  }
};
